/**
 * Tool Card Component
 * Displays a single tool in the toolbox grid
 */

import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MoreVertical, Pencil, Trash2, Wrench } from 'lucide-react';
import { ToolCategoryBadge } from './ToolCategoryBadge';
import { useDeleteTool } from '@/hooks/useToolbox';
import type { TechnicianToolWithMedia } from '@/types/toolbox';

interface ToolCardProps {
  tool: TechnicianToolWithMedia;
  isEditable: boolean;
  onEdit: (tool: TechnicianToolWithMedia) => void;
}

export function ToolCard({ tool, isEditable, onEdit }: ToolCardProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const deleteTool = useDeleteTool();

  const handleDelete = () => {
    deleteTool.mutate({
      toolId: tool.id,
      technicianProfileId: tool.technician_profile_id,
    });
    setShowDeleteDialog(false);
  };

  return (
    <>
      <Card className="group overflow-hidden hover:shadow-md transition-shadow">
        {/* Photo Area */}
        <div className="aspect-square bg-muted relative overflow-hidden">
          {tool.primary_photo_url ? (
            <img
              src={tool.primary_photo_url}
              alt={tool.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Wrench className="h-12 w-12 text-muted-foreground/40" />
            </div>
          )}

          {/* Quick Actions Overlay */}
          {isEditable && (
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" size="icon" className="h-8 w-8 shadow-md">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(tool)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setShowDeleteDialog(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {/* Content */}
        <CardContent className="p-3 space-y-2">
          <h3 className="font-medium text-sm line-clamp-1">{tool.name}</h3>
          
          <div className="flex items-center gap-2 flex-wrap">
            <ToolCategoryBadge category={tool.category} />
            {/* Core-100 R2 (CORE-K): asserted ownership vs a document on file — never presented as checked by anyone. */}
            <span className="text-[10px] text-muted-foreground" data-testid="tool-evidence">
              {(tool as { source_document_id?: string | null }).source_document_id ? 'Purchase document on file' : 'Self-reported'}
            </span>
            {tool.brand && (
              <span className="text-xs text-muted-foreground">{tool.brand}</span>
            )}
          </div>

          {tool.model && (
            <p className="text-xs text-muted-foreground line-clamp-1">
              Model: {tool.model}
            </p>
          )}

          {tool.purchase_price != null && tool.purchase_price > 0 && (
            <p className="text-sm font-semibold text-primary">
              ${tool.purchase_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tool</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{tool.name}" from your toolbox? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
